import { useCallback, useEffect, useRef, useState } from 'react';
import type { PendingPage } from '@shared/api';

/**
 * docs/custom-page.md "Pending page": the placeholder shown in the grid and
 * present mode from the moment a page is known to be coming until it either
 * lands (a real section with this id) or the server reports it never will.
 * `status` tracks each key's own lifecycle independently of the others.
 */
export type PendingEntryStatus = 'pending' | 'added' | 'not_added';

export interface PendingEntry {
  pending: PendingPage;
  status: PendingEntryStatus;
  sectionIds?: string[];
}

export type PendingPagesState = readonly PendingEntry[];

/** How long a resolved-but-unbuilt 'not_added' placeholder stays visible before it's removed. */
export const NOT_ADDED_COLLAPSE_MS = 1500;

/**
 * `page_pending` — insert a new placeholder, or replace an existing one with
 * the same key in place (api.ts `PendingPage`: "a later `page_pending` with
 * the same key ... replaces the earlier one"). Replacing resets status to
 * 'pending' — a key only reaches 'added'/'not_added' via `applyPageResolved`.
 */
export function applyPagePending(state: PendingPagesState, pending: PendingPage): PendingPagesState {
  const index = state.findIndex((entry) => entry.pending.key === pending.key);
  const entry: PendingEntry = { pending, status: 'pending' };
  if (index === -1) return [...state, entry];
  const next = [...state];
  next[index] = entry;
  return next;
}

/** `page_resolved` — mark the key's outcome. A missing key is a no-op (nothing to resolve). */
export function applyPageResolved(
  state: PendingPagesState,
  key: string,
  outcome: 'added' | 'not_added',
  sectionIds?: string[],
): PendingPagesState {
  let changed = false;
  const next = state.map((entry) => {
    if (entry.pending.key !== key) return entry;
    changed = true;
    return { ...entry, status: outcome, sectionIds };
  });
  return changed ? next : state;
}

/**
 * Turn end (`done`/`error`): every key should already carry a resolution by
 * then (api.ts comment) — this only drops the defensive edge case of one
 * that somehow never did. An 'added' entry waiting for its section to land,
 * or a 'not_added' entry mid-collapse, is untouched; they finish their own
 * lifecycle (`resolveAddedAgainstSections` / the collapse timer).
 */
export function dropUnresolved(state: PendingPagesState): PendingPagesState {
  if (state.every((entry) => entry.status !== 'pending')) return state;
  return state.filter((entry) => entry.status !== 'pending');
}

/**
 * Once the real page is in the document, the placeholder can go — but not
 * before, or the grid reflows as the card vanishes ahead of its
 * replacement landing in the same slot (docs/custom-page.md).
 */
export function resolveAddedAgainstSections(
  state: PendingPagesState,
  presentSectionIds: ReadonlySet<string>,
): PendingPagesState {
  let changed = false;
  const next = state.filter((entry) => {
    const firstId = entry.sectionIds?.[0];
    const landed = entry.status === 'added' && firstId !== undefined && presentSectionIds.has(firstId);
    if (landed) changed = true;
    return !landed;
  });
  return changed ? next : state;
}

export function removeEntry(state: PendingPagesState, key: string): PendingPagesState {
  if (!state.some((entry) => entry.pending.key === key)) return state;
  return state.filter((entry) => entry.pending.key !== key);
}

/** One slot in a sequence (the grid's `allSections`, or the gallery's `visibleSections`) with pending placeholders spliced in. */
export type SequenceItem<T> =
  | { kind: 'item'; key: string; value: T }
  | { kind: 'pending'; key: string; entry: PendingEntry };

/**
 * Splices every pending entry into `items` right after its `afterSectionId`
 * (or at the end when absent or not found — api.ts `PendingPage.afterSectionId`).
 * Entries are applied in array order so two placeholders queued at the same
 * spot stack in arrival order. Pure so grid/gallery ordering is directly
 * testable without mounting either component.
 */
export function mergeSequence<T extends { id: string }>(
  items: readonly T[],
  entries: PendingPagesState,
): SequenceItem<T>[] {
  const result: SequenceItem<T>[] = items.map((value) => ({ kind: 'item', key: value.id, value }));
  for (const entry of entries) {
    const afterId = entry.pending.afterSectionId;
    const index = afterId
      ? result.findIndex((slot) => slot.kind === 'item' && slot.key === afterId)
      : -1;
    let insertAt = index === -1 ? result.length : index + 1;
    // Walk past any placeholder already stacked at this same anchor so two
    // pendings queued at the same spot land in arrival order, nearest one
    // last (rather than each new one shoving the last to the back).
    if (afterId) {
      while (insertAt < result.length) {
        const slot = result[insertAt];
        if (slot?.kind === 'pending' && slot.entry.pending.afterSectionId === afterId) insertAt += 1;
        else break;
      }
    }
    result.splice(insertAt, 0, { kind: 'pending', key: entry.pending.key, entry });
  }
  return result;
}

/**
 * Present mode (docs/custom-page.md "What the rep sees"): a placeholder
 * pulls the viewer forward only when the rep is currently looking at the
 * page it's being inserted after. Viewing anything else, the placeholder
 * just joins the sequence without moving anyone.
 */
export function pendingArrivalAdvances(activeSectionId: string | null, pending: PendingPage): boolean {
  return activeSectionId !== null && pending.afterSectionId === activeSectionId;
}

export interface PendingPagesHandle {
  entries: PendingPagesState;
  onPagePending: (pending: PendingPage) => void;
  onPageResolved: (key: string, outcome: 'added' | 'not_added', sectionIds?: string[]) => void;
  /** Call on the chat stream's `done`/`error`. */
  onChatSettled: () => void;
}

/**
 * Owns the pending-page placeholder lifecycle across a turn: upsert on
 * `page_pending`, resolve on `page_resolved`, drop leftovers at `done`/
 * `error`, collapse a 'not_added' entry after ~1.5s, and clear an 'added'
 * entry once `documentSectionIds` (the live proposal) actually contains it.
 */
export function usePendingPages(documentSectionIds: readonly string[]): PendingPagesHandle {
  const [entries, setEntries] = useState<PendingPagesState>([]);
  const collapseTimers = useRef(new Map<string, number>());

  const onPagePending = useCallback((pending: PendingPage) => {
    setEntries((prev) => applyPagePending(prev, pending));
  }, []);

  const onPageResolved = useCallback(
    (key: string, outcome: 'added' | 'not_added', sectionIds?: string[]) => {
      setEntries((prev) => applyPageResolved(prev, key, outcome, sectionIds));
      if (outcome === 'not_added') {
        const existing = collapseTimers.current.get(key);
        if (existing !== undefined) window.clearTimeout(existing);
        const timer = window.setTimeout(() => {
          setEntries((prev) => removeEntry(prev, key));
          collapseTimers.current.delete(key);
        }, NOT_ADDED_COLLAPSE_MS);
        collapseTimers.current.set(key, timer);
      }
    },
    [],
  );

  const onChatSettled = useCallback(() => {
    setEntries((prev) => dropUnresolved(prev));
  }, []);

  useEffect(() => {
    const present = new Set(documentSectionIds);
    setEntries((prev) => resolveAddedAgainstSections(prev, present));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentSectionIds.join('\u0000')]);

  useEffect(() => {
    const timers = collapseTimers.current;
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return { entries, onPagePending, onPageResolved, onChatSettled };
}
